import UserAvatar from '@/components/User/UserAvatar';
import { useCompactSidebar } from '@/hooks/useCompactSidebar';
import { getOrganisations, logout, switchOrganisation } from '@/services/api';
import { ContactIcon, LogoutIcon, ReportBugIcon } from '@/utils/helper';
import useMobile from '@/utils/useMobile';
import { AppstoreOutlined, EllipsisOutlined, InboxOutlined } from '@ant-design/icons';
import { ModalForm, ProFormSelect, ProFormText, ProFormTextArea } from '@ant-design/pro-components';
import { useEmotionCss } from '@ant-design/use-emotion-css';
import { FormattedMessage, history, request, useIntl, useModel } from '@umijs/max';
import {
  Button,
  Col,
  ConfigProvider,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  message,
  Popover,
  Row,
  Space,
  Spin,
  theme,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { Colors } from '../../../config/colors';
import LanguageSwitcher from '../LanguageSwitcher';
import { ReceiptModal } from './ReceiptModal';
import './RightActions.less';

const { Title } = Typography;

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
  collapsed?: boolean;
};

type OrganisationSwitcherProps = GlobalHeaderRightProps & {
  contentOnly?: boolean;
  showWhenSingle?: boolean;
};

type FooterActionProps = {
  collapsed?: boolean;
  actionClassName?: string;
  iconColor?: string;
  iconSize?: number;
};

type UserGuideButtonProps = FooterActionProps & {
  labelOverride?: string;
};

const AngelInsiderIcon: FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M13.1019 2.08249L6.76822 0.43457L0.435059 2.08249V16.4346L6.76822 14.7867M6.76822 0.43457V14.7867M13.1019 2.08249L19.4351 0.43457V14.7867L13.1019 16.4346L6.76822 14.7867M13.1019 2.08249V16.4346"
      stroke="currentColor"
      strokeWidth="0.87"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const OrganisationSwitcher: FC<OrganisationSwitcherProps> = ({
  collapsed,
  contentOnly = false,
  showWhenSingle = false,
}) => {
  const { initialState } = useModel('@@initialState');
  const [organisations, setOrganisations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const intl = useIntl();

  const fetchOrgs = async () => {
    try {
      const res = await getOrganisations();
      if (res && res.data) {
        setOrganisations(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      console.error('Failed to fetch organisations', e);
    }
  };

  useEffect(() => {
    if (initialState?.currentUser) {
      fetchOrgs();
    }
  }, [initialState?.currentUser]);

  const handleSwitch = async (orgId: string) => {
    if (orgId === initialState?.currentUser?.currentOrganisationId) return;
    setLoading(true);
    const hide = message.loading(
      intl.formatMessage({
        id: 'components.rightContent.organisationSwitcher.switching',
        defaultMessage: 'Switching organisation...',
      }),
      0,
    );
    try {
      const res = await switchOrganisation(orgId);
      if (res.accessToken) {
        localStorage.setItem('token', res.accessToken);
        if (res.refreshToken) {
          localStorage.setItem('tokenRefresh', res.refreshToken);
        }
        hide();
        message.success(
          intl.formatMessage({
            id: 'components.rightContent.organisationSwitcher.switchSuccess',
            defaultMessage: 'Switched organisation successfully',
          }),
        );
        // Reload page to ensure clean state with new token
        window.location.reload();
      } else {
        hide();
        message.warning(
          intl.formatMessage({
            id: 'components.rightContent.organisationSwitcher.switchNoToken',
            defaultMessage: 'Switching organisation did not return new token',
          }),
        );
      }
    } catch (e: any) {
      hide();
      console.error(e);
      message.error(
        e.message ||
          intl.formatMessage({
            id: 'components.rightContent.organisationSwitcher.switchError',
            defaultMessage: 'Failed to switch organisation',
          }),
      );
    } finally {
      setLoading(false);
    }
  };

  const currentUser = initialState?.currentUser;
  const currentUserRecord = currentUser as
    | (typeof currentUser & { organisationName?: string; organizationName?: string })
    | undefined;
  const currentOrgId = currentUser?.currentOrganisationId;
  const emailDomain = currentUser?.email?.split('@')[1];
  const currentOrg = organisations.find((o) => (o.id || o.organisationId) === currentOrgId) ||
    organisations[0] || {
      name:
        currentUserRecord?.organisationName ??
        currentUserRecord?.organizationName ??
        emailDomain ??
        intl.formatMessage({
          id: 'components.rightContent.organisationSwitcher.unknownOrg',
          defaultMessage: 'Unknown Org',
        }),
    };
  const hasSwitchableOrganisations = organisations.length > 1;
  const visibleOrganisations = organisations.length > 0 ? organisations : [currentOrg];

  if (!contentOnly && !showWhenSingle && !hasSwitchableOrganisations) return null;

  const items: MenuProps['items'] = organisations.map((org) => ({
    key: org.id || org.organisationId,
    label: org.name || org.title || org.id || org.organisationId, // Fallback
    onClick: () => handleSwitch(org.id || org.organisationId),
    disabled: (org.id || org.organisationId) === currentOrgId,
  }));

  // Helper to trigger dropdown
  const TriggerButton = (props: any) => (
    <Button
      type="text"
      style={{ color: Colors.logoutColor, ...props.style }}
      className="logoutButton"
      icon={<AppstoreOutlined />} // Use an icon for Org
      loading={loading}
      {...props}
    >
      {!collapsed && !showToopTip && (
        <span style={{ fontSize: 14, marginLeft: 8 }}>
          {currentOrg.name ||
            currentOrg.title ||
            intl.formatMessage({
              id: 'components.rightContent.organisationSwitcher.defaultLabel',
              defaultMessage: 'Org',
            })}
        </span>
      )}
    </Button>
  );

  if (contentOnly) {
    return (
      <div className="organisation-switcher-inline-list">
        {visibleOrganisations.map((org) => {
          const orgId = org.id || org.organisationId;
          const label = org.name || org.title || orgId;
          const isCurrent = orgId === currentOrgId || visibleOrganisations.length === 1;
          return (
            <button
              key={orgId || label}
              type="button"
              className={`organisation-switcher-inline-item ${
                isCurrent
                  ? 'organisation-switcher-inline-item-current'
                  : 'organisation-switcher-inline-item-switchable'
              }`}
              aria-current={isCurrent ? 'true' : undefined}
              disabled={isCurrent}
              onClick={() => handleSwitch(orgId)}
            >
              <AppstoreOutlined className="organisation-switcher-inline-icon" />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (!hasSwitchableOrganisations) {
    return (
      <span>
        <TriggerButton />
      </span>
    );
  }

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      {showToopTip || collapsed ? (
        <Tooltip
          title={
            currentOrg.name ||
            currentOrg.title ||
            intl.formatMessage({
              id: 'components.rightContent.organisationSwitcher.tooltipFallback',
              defaultMessage: 'Switch Organisation',
            })
          }
          placement="right"
        >
          <span>
            <TriggerButton />
          </span>
        </Tooltip>
      ) : (
        <span>
          <TriggerButton />
        </span>
      )}
    </Dropdown>
  );
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return (
    <div style={{ color: Colors.textColorSecondary, fontWeight: 600 }}>{currentUser?.username}</div>
  );
};

export const UserGuideButton: FC<UserGuideButtonProps> = ({
  actionClassName,
  collapsed,
  iconColor,
  iconSize = 16,
  labelOverride,
}) => {
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const intl = useIntl();
  const label =
    labelOverride ??
    intl.formatMessage({
      id: 'components.rightContent.userGuideButton.label',
      defaultMessage: 'A&GEL Insider',
    });

  const handleUserGuideClick = () => {
    history.push('/angel-insider');
  };
  const icon = <AngelInsiderIcon size={iconSize} />;

  return showToopTip || collapsed ? (
    <Tooltip title={label} placement="right">
      <Button
        type="text"
        onClick={handleUserGuideClick}
        style={{ color: iconColor ?? Colors.logoutColor }}
        className={`logoutButton ${actionClassName ?? ''}`}
        icon={icon}
        aria-label={label}
      ></Button>
    </Tooltip>
  ) : (
    <Button
      type="text"
      onClick={handleUserGuideClick}
      style={{ color: iconColor ?? Colors.logoutColor }}
      className={`logoutButton ${actionClassName ?? ''}`}
      icon={icon}
    >
      <span style={{ fontSize: 14 }}>{label}</span>
    </Button>
  );
};

export const AngelTeamsButton: FC<FooterActionProps> = ({
  actionClassName,
  collapsed,
  iconColor,
  iconSize = 16,
}) => {
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const { initialState } = useModel('@@initialState');
  const contactUsUrl = initialState?.userMeta?.contactUs?.url;
  const intl = useIntl();

  const handleContactUsClick = () => {
    if (contactUsUrl) {
      window.open(contactUsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return showToopTip || collapsed ? (
    <Tooltip
      title={intl.formatMessage({
        id: 'components.rightContent.angelTeamsButton.label',
        defaultMessage: 'Contact Us',
      })}
      placement="right"
    >
      <Button
        type="text"
        onClick={handleContactUsClick}
        style={{ color: iconColor ?? Colors.logoutColor }}
        className={`logoutButton ${actionClassName ?? ''}`}
        icon={<ContactIcon size={iconSize} color={iconColor} />}
        aria-label={intl.formatMessage({
          id: 'components.rightContent.angelTeamsButton.label',
          defaultMessage: 'Contact Us',
        })}
      ></Button>
    </Tooltip>
  ) : (
    <Button
      type="text"
      onClick={handleContactUsClick}
      style={{ color: iconColor ?? Colors.logoutColor }}
      className={`logoutButton ${actionClassName ?? ''}`}
      icon={<ContactIcon size={iconSize} color={iconColor} />}
    >
      <span style={{ fontSize: 14 }}>
        <FormattedMessage
          id="components.rightContent.angelTeamsButton.label"
          defaultMessage="Contact Us"
        />
      </span>
    </Button>
  );
};

export const ReportBugButton: FC<FooterActionProps> = ({
  actionClassName,
  collapsed,
  iconColor,
  iconSize,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isReceiptVisible, setIsReceiptVisible] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const formRef = useRef<any>();
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const { initialState } = useModel('@@initialState');
  const { token } = theme.useToken();
  const intl = useIntl();
  const receiptModalClassName = 'receipt-modal';
  const bugReportModalClassName = 'bug-report-modal';

  // Builds a `{ label, value }` option from an intl id/defaultMessage - `value`
  // defaults to the (English) defaultMessage, which matches every entry below.
  const opt = (id: string, defaultMessage: string, value: string = defaultMessage) => ({
    label: intl.formatMessage({ id, defaultMessage }),
    value,
  });

  // Available pages in the application
  const availablePages = [
    opt('components.rightContent.reportBug.page.home', 'Home'),
    opt('components.rightContent.reportBug.page.chat', 'Chat'),
    opt('components.rightContent.reportBug.page.composer', 'Composer'),
    opt('components.rightContent.reportBug.page.review', 'Review'),
    opt('components.rightContent.reportBug.page.reference', 'Reference'),
    opt('components.rightContent.reportBug.page.files', 'Files'),
    opt('common.settings', 'Settings'),
    opt('components.rightContent.reportBug.page.userProfile', 'User Profile'),
    opt('components.rightContent.reportBug.page.other', 'Other'),
  ];

  // Popular browsers
  const popularBrowsers = [
    opt('components.rightContent.reportBug.browser.chrome', 'Chrome'),
    opt('components.rightContent.reportBug.browser.safari', 'Safari'),
    opt('components.rightContent.reportBug.browser.firefox', 'Firefox'),
    opt('components.rightContent.reportBug.browser.edge', 'Edge'),
    opt('components.rightContent.reportBug.browser.opera', 'Opera'),
    opt('components.rightContent.reportBug.browser.brave', 'Brave'),
    opt('components.rightContent.reportBug.browser.arc', 'Arc'),
    opt('components.rightContent.reportBug.browser.other', 'Other'),
  ];

  const detectDevice = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'Tablet';
    }
    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua,
      )
    ) {
      return 'Mobile';
    }
    return 'Desktop';
  };

  const detectOS = () => {
    const ua = navigator.userAgent;
    const platform = navigator.platform;

    if (/Win/i.test(platform)) {
      if (/Windows NT 10/i.test(ua)) return 'Windows 10/11';
      if (/Windows NT 6.3/i.test(ua)) return 'Windows 8.1';
      if (/Windows NT 6.2/i.test(ua)) return 'Windows 8';
      if (/Windows NT 6.1/i.test(ua)) return 'Windows 7';
      return 'Windows';
    }
    if (/Mac/i.test(platform)) {
      const match = ua.match(/Mac OS X ([0-9_.]+)/);
      if (match) {
        return `macOS ${match[1].replace(/_/g, '.')}`;
      }
      return 'macOS';
    }
    if (/iPhone|iPad|iPod/i.test(ua)) {
      const match = ua.match(/OS ([0-9_]+)/);
      if (match) {
        return `iOS ${match[1].replace(/_/g, '.')}`;
      }
      return 'iOS';
    }
    if (/Android/i.test(ua)) {
      const match = ua.match(/Android ([0-9.]+)/);
      if (match) {
        return `Android ${match[1]}`;
      }
      return 'Android';
    }
    if (/Linux/i.test(platform)) {
      return 'Linux';
    }
    return 'Unknown OS';
  };

  const detectBrowser = () => {
    const ua = navigator.userAgent;

    if (/Edg/i.test(ua)) {
      return 'Edge';
    }
    if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) {
      return 'Chrome';
    }
    if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
      return 'Safari';
    }
    if (/Firefox/i.test(ua)) {
      return 'Firefox';
    }
    if (/OPR|Opera/i.test(ua)) {
      return 'Opera';
    }
    if (/Brave/i.test(ua)) {
      return 'Brave';
    }
    return 'Other';
  };

  const getPageTitle = () => {
    const pathname = window.location.pathname;

    // Map common paths to friendly names
    const pathMap: { [key: string]: string } = {
      '/': 'Home',
      '/chat': 'Chat',
      '/review': 'Review',
      '/reference': 'Reference',
      '/file': 'Files',
      '/files': 'Files',
      '/user': 'User',
      '/settings': 'Settings',
    };

    // Check for exact match
    if (pathMap[pathname]) {
      return pathMap[pathname];
    }

    // Check for partial match (e.g., /chat/123 -> Chat)
    for (const [path, title] of Object.entries(pathMap)) {
      if (pathname.startsWith(path) && path !== '/') {
        return title;
      }
    }

    // Fallback: capitalize first segment
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      return segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
    }

    return 'Unknown Page';
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const getInitialValues = () => {
    return {
      name: initialState?.currentUser?.username || '',
      email: initialState?.currentUser?.email || '',
      bugLocation: getPageTitle(),
      device: detectDevice(),
      os: detectOS(),
      browser: detectBrowser(),
    };
  };

  const handleSubmit = async (values: any) => {
    try {
      const bugReportData = {
        name: values.name,
        email: values.email,
        bugLocation: values.bugLocation,
        bugDescription: values.bugDescription,
        device: values.device,
        os: values.os,
        browser: values.browser,
        files: uploadedFileUrls,
      };

      await request('/api/bot/bug', {
        method: 'POST',
        data: bugReportData,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      message.success(
        intl.formatMessage({
          id: 'components.rightContent.reportBug.submitSuccess',
          defaultMessage: 'Bug report submitted successfully!',
        }),
      );
      setFileList([]);
      setUploadedFileUrls([]);
      setIsModalVisible(false);
      setIsReceiptVisible(true);
      return true;
    } catch (error: any) {
      console.error('Failed to submit bug report:', error);
      message.error(
        error?.response?.data?.message ||
          intl.formatMessage({
            id: 'components.rightContent.reportBug.submitError',
            defaultMessage: 'Failed to submit bug report. Please try again.',
          }),
      );
      return false;
    }
  };

  const handleFileUpload = async (file: any) => {
    const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
    if (!isValidType) {
      message.error(
        intl.formatMessage({
          id: 'components.rightContent.reportBug.invalidFileType',
          defaultMessage: 'You can only upload image or video files!',
        }),
      );
      return false;
    }

    const pendingFile = {
      ...file,
      displayName: file.name,
      status: 'uploading',
    };
    setFileList((prev) => [...prev, pendingFile]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await request('/api/upload/', {
        method: 'POST',
        data: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      setFileList((prev) =>
        prev.map((item) =>
          item.uid === file.uid
            ? {
                ...item,
                displayName: file.name,
                status: 'done',
                url: response.url,
                thumbUrl: response.url,
              }
            : item,
        ),
      );
      setUploadedFileUrls((prev) => [...prev, response.url]);
      message.success(
        intl.formatMessage(
          {
            id: 'components.rightContent.reportBug.fileUploadSuccess',
            defaultMessage: 'File "{fileName}" uploaded successfully!',
          },
          { fileName: file.name },
        ),
      );
      return false;
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error(
        intl.formatMessage({
          id: 'components.rightContent.reportBug.fileUploadError',
          defaultMessage: 'Failed to upload file',
        }),
      );
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
      return false;
    }
  };

  const uploadProps = {
    beforeUpload: handleFileUpload,
    onRemove: (file: any) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));

      const urlToRemove = file.url ?? file.thumbUrl;
      if (urlToRemove) {
        setUploadedFileUrls((prev) => prev.filter((url) => url !== urlToRemove));
      }
    },
    fileList,
  };

  return (
    <>
      {showToopTip || collapsed ? (
        <Tooltip
          title={intl.formatMessage({
            id: 'components.rightContent.reportBug.label',
            defaultMessage: 'Report a bug',
          })}
          placement="right"
        >
          <Button
            type="text"
            onClick={handleOpenModal}
            style={{ color: iconColor ?? Colors.errorColor }}
            className={`logoutButton ${actionClassName ?? ''}`}
            icon={<ReportBugIcon size={iconSize} color="#CC5959" />}
            aria-label={intl.formatMessage({
              id: 'components.rightContent.reportBug.label',
              defaultMessage: 'Report a bug',
            })}
          />
        </Tooltip>
      ) : (
        <Button
          type="text"
          onClick={handleOpenModal}
          style={{ color: iconColor ?? Colors.errorColor }}
          className={`logoutButton ${actionClassName ?? ''}`}
          icon={
            <span style={{ display: 'inline-flex', transform: 'translateY(-3px)' }}>
              <ReportBugIcon size={iconSize} color="#CC5959" />
            </span>
          }
        >
          <span style={{ fontSize: 14 }}>
            <FormattedMessage
              id="components.rightContent.reportBug.label"
              defaultMessage="Report a bug"
            />
          </span>
        </Button>
      )}
      <ConfigProvider
        theme={{
          token: {
            colorText: token.colorTextBase,
            colorTextLabel: token.colorTextBase,
            colorTextHeading: token.colorTextBase,
          },
        }}
      >
        <ModalForm
          title={
            <div className="bug-report-modal-title">
              <div className="bug-report-modal-title-main">
                <FormattedMessage
                  id="components.rightContent.reportBug.label"
                  defaultMessage="Report a bug"
                />
              </div>
              <div className="bug-report-modal-title-copy">
                <FormattedMessage
                  id="components.rightContent.reportBug.modalDescription"
                  defaultMessage="Tell us what you've encountered by filling in the form below."
                />
              </div>
            </div>
          }
          open={isModalVisible}
          formRef={formRef}
          onOpenChange={(visible) => {
            setIsModalVisible(visible);
            if (visible) {
              // Set values when modal opens
              setTimeout(() => {
                formRef.current?.setFieldsValue(getInitialValues());
              }, 100);
            }
          }}
          onFinish={handleSubmit}
          width={680}
          modalProps={{
            centered: true,
            destroyOnClose: true,
            className: bugReportModalClassName,
          }}
          submitter={{
            render: (_, dom) => <div className="bug-report-submit">{dom[1]}</div>,
            searchConfig: {
              submitText: intl.formatMessage({ id: 'common.submit', defaultMessage: 'Submit' }),
            },
          }}
        >
          <div className="bug-report-form">
            <section className="bug-report-section">
              <div className="bug-report-section-header">
                <Title level={5} className="bug-report-section-title">
                  <FormattedMessage
                    id="components.rightContent.reportBug.contactSectionTitle"
                    defaultMessage="Contact"
                  />
                </Title>
                <span className="bug-report-section-rule" />
              </div>
              <Row gutter={4} className="bug-report-contact-row">
                <Col span={12}>
                  <div className="bug-report-field bug-report-contact-field">
                    <ProFormText
                      name="name"
                      label={intl.formatMessage({ id: 'common.name', defaultMessage: 'Name' })}
                      placeholder={intl.formatMessage({
                        id: 'components.rightContent.reportBug.namePlaceholder',
                        defaultMessage: 'Enter your name',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'components.rightContent.reportBug.nameRequired',
                            defaultMessage: 'Please enter your name',
                          }),
                        },
                      ]}
                      fieldProps={{
                        variant: 'borderless',
                      }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="bug-report-field bug-report-contact-field">
                    <ProFormText
                      name="email"
                      label={intl.formatMessage({
                        id: 'components.rightContent.reportBug.emailLabel',
                        defaultMessage: 'Email',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'components.rightContent.reportBug.emailPlaceholder',
                        defaultMessage: 'Enter your email',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'components.rightContent.reportBug.emailRequired',
                            defaultMessage: 'Please enter your email',
                          }),
                        },
                        {
                          type: 'email',
                          message: intl.formatMessage({
                            id: 'components.rightContent.reportBug.emailInvalid',
                            defaultMessage: 'Please enter a valid email',
                          }),
                        },
                      ]}
                      fieldProps={{
                        variant: 'borderless',
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </section>

            <section className="bug-report-section">
              <div className="bug-report-section-header">
                <Title level={5} className="bug-report-section-title">
                  <FormattedMessage
                    id="components.rightContent.reportBug.bugDescriptionSectionTitle"
                    defaultMessage="Bug Description"
                  />
                </Title>
                <span className="bug-report-section-rule" />
              </div>
              <div className="bug-report-field">
                <ProFormSelect
                  name="bugLocation"
                  label={intl.formatMessage({
                    id: 'components.rightContent.reportBug.locationLabel',
                    defaultMessage: 'Where was the bug found?',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'components.rightContent.reportBug.locationPlaceholder',
                    defaultMessage: 'Select Function',
                  })}
                  options={availablePages}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'components.rightContent.reportBug.locationRequired',
                        defaultMessage: 'Please specify where the bug was found',
                      }),
                    },
                  ]}
                  fieldProps={{
                    variant: 'borderless',
                  }}
                />
              </div>

              <div className="bug-report-field">
                <ProFormTextArea
                  name="bugDescription"
                  label={intl.formatMessage({
                    id: 'components.rightContent.reportBug.descriptionLabel',
                    defaultMessage: 'Describe the issue',
                  })}
                  placeholder="✎"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'components.rightContent.reportBug.descriptionRequired',
                        defaultMessage: 'Please describe the issue',
                      }),
                    },
                  ]}
                  fieldProps={{
                    rows: 2,
                    variant: 'borderless',
                  }}
                />
              </div>

              <div className="bug-report-field">
                <Form.Item
                  label={intl.formatMessage({
                    id: 'components.rightContent.reportBug.filesLabel',
                    defaultMessage: 'Screenshots or Recordings',
                  })}
                  name="files"
                  style={{ marginBottom: 0 }}
                >
                  <Upload.Dragger
                    accept="image/*,video/*"
                    {...uploadProps}
                    multiple
                    showUploadList={false}
                    className="bug-report-upload"
                  >
                    <p className="bug-report-upload-icon">
                      <InboxOutlined />
                    </p>
                    <p className="bug-report-upload-text">
                      <FormattedMessage
                        id="components.rightContent.reportBug.uploadText"
                        defaultMessage="Click or drag to upload"
                      />
                    </p>
                    {fileList.length > 0 && (
                      <div className="bug-report-upload-list">
                        {fileList.map((file) => {
                          const fileSizeKb =
                            typeof file.size === 'number' ? Math.ceil(file.size / 1024) : null;
                          const fileUrl = file.url || file.thumbUrl;
                          const displayName = file.displayName || file.name;

                          return (
                            <div className="bug-report-upload-item" key={file.uid}>
                              <div className="bug-report-upload-thumb">
                                {fileUrl ? (
                                  <img src={fileUrl} alt={file.name} />
                                ) : (
                                  <AppstoreOutlined />
                                )}
                              </div>
                              <div className="bug-report-upload-meta">
                                {fileUrl ? (
                                  <a
                                    className="bug-report-upload-name"
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {displayName}
                                  </a>
                                ) : (
                                  <span className="bug-report-upload-name">{displayName}</span>
                                )}
                                {fileSizeKb !== null && (
                                  <span className="bug-report-upload-size">
                                    <FormattedMessage
                                      id="components.rightContent.reportBug.fileSizeKb"
                                      defaultMessage="{size} KB"
                                      values={{ size: fileSizeKb }}
                                    />
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="bug-report-upload-remove"
                                aria-label={intl.formatMessage(
                                  {
                                    id: 'components.rightContent.reportBug.removeFile',
                                    defaultMessage: 'Remove {fileName}',
                                  },
                                  { fileName: file.name },
                                )}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (uploadProps.onRemove) {
                                    uploadProps.onRemove(file);
                                  }
                                }}
                              >
                                x
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Upload.Dragger>
                </Form.Item>
              </div>
            </section>

            <section className="bug-report-section">
              <div className="bug-report-section-header">
                <Title level={5} className="bug-report-section-title">
                  <FormattedMessage
                    id="components.rightContent.reportBug.environmentSectionTitle"
                    defaultMessage="Environment"
                  />
                </Title>
                <span className="bug-report-section-rule" />
              </div>
              <Row gutter={4}>
                <Col span={12}>
                  <div className="bug-report-field">
                    <ProFormText
                      name="device"
                      label={intl.formatMessage({
                        id: 'components.rightContent.reportBug.deviceLabel',
                        defaultMessage: 'Device',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'components.rightContent.reportBug.devicePlaceholder',
                        defaultMessage: 'MacBook Pro M1 2021',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'components.rightContent.reportBug.deviceRequired',
                            defaultMessage: 'Please specify your device',
                          }),
                        },
                      ]}
                      fieldProps={{
                        variant: 'borderless',
                      }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="bug-report-field">
                    <ProFormText
                      name="os"
                      label={intl.formatMessage({
                        id: 'components.rightContent.reportBug.osLabel',
                        defaultMessage: 'Operating system and version',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'components.rightContent.reportBug.osPlaceholder',
                        defaultMessage: 'macOS Sequoia 15.1',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'components.rightContent.reportBug.osRequired',
                            defaultMessage: 'Please specify your OS',
                          }),
                        },
                      ]}
                      fieldProps={{
                        variant: 'borderless',
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <div className="bug-report-field">
                <ProFormSelect
                  name="browser"
                  label={intl.formatMessage({
                    id: 'components.rightContent.reportBug.browserLabel',
                    defaultMessage: 'Browser',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'components.rightContent.reportBug.browserPlaceholder',
                    defaultMessage: 'Select Browser',
                  })}
                  options={popularBrowsers}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'components.rightContent.reportBug.browserRequired',
                        defaultMessage: 'Please specify your browser',
                      }),
                    },
                  ]}
                  fieldProps={{
                    variant: 'borderless',
                  }}
                />
              </div>
            </section>
          </div>
        </ModalForm>
      </ConfigProvider>
      <ReceiptModal
        open={isReceiptVisible}
        onClose={() => setIsReceiptVisible(false)}
        className={receiptModalClassName}
        width={640}
      />
    </>
  );
};

const SidebarProfilePopoverContent: FC<{ accountEmail: string }> = ({ accountEmail }) => (
  <div className="sidebar-user-footer-popover-content">
    <div className="sidebar-user-footer-popover-email" title={accountEmail}>
      {accountEmail}
    </div>
    <Divider className="sidebar-user-footer-popover-divider" />
    <div className="sidebar-user-footer-popover-organisation">
      <OrganisationSwitcher collapsed={false} contentOnly showWhenSingle />
    </div>
  </div>
);

export const SidebarActions: FC<GlobalHeaderRightProps> = ({ collapsed }) => {
  const isMobile = useMobile();

  if (isMobile) {
    return (
      <Space direction="vertical" align="center" style={{ width: '100%', marginBottom: 16 }}>
        <Popover
          content={
            <Space direction="vertical">
              <OrganisationSwitcher collapsed={false} />
              <UserGuideButton collapsed={false} />
              <AngelTeamsButton collapsed={false} />
              <LanguageSwitcher collapsed={false} />
              {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
              <LogoutButton collapsed={false} />
              <ReportBugButton collapsed={false} />
            </Space>
          }
          placement="rightBottom"
          trigger="click"
        >
          <Button
            type="text"
            icon={<EllipsisOutlined style={{ fontSize: 24, color: Colors.logoutColor }} />}
          />
        </Popover>
      </Space>
    );
  }

  return (
    <Space
      direction="vertical"
      size={4}
      align="start"
      style={{
        display: 'flex',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        alignItems: 'stretch',
      }}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <SidebarUserFooter collapsed={collapsed} />
    </Space>
  );
};

const SidebarUserFooter: FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { initialState } = useModel('@@initialState');
  const isCompactSidebar = useCompactSidebar();
  const expandedAvatarSize = isCompactSidebar ? 24 : 34;
  const collapsedAvatarSize = isCompactSidebar ? 28 : 34;
  const avatarSize = collapsed ? collapsedAvatarSize : expandedAvatarSize;
  const primaryFooterIconSize = isCompactSidebar ? 12 : 15;
  const userGuideIconSize = isCompactSidebar ? 12 : 16;
  const [organisations, setOrganisations] = useState<any[]>([]);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [profilePopoverWidth, setProfilePopoverWidth] = useState<number>();
  const profileRef = useRef<HTMLDivElement>(null);
  const currentUser = initialState?.currentUser;
  const displayName = currentUser?.name ?? currentUser?.username ?? currentUser?.email ?? '';
  const initial = displayName.trim().charAt(0).toUpperCase() || '?';
  const currentOrg = organisations.find(
    (org) => (org.id || org.organisationId) === currentUser?.currentOrganisationId,
  );
  const emailDomain = currentUser?.email?.split('@')[1];
  const organisationName = currentOrg?.name ?? currentOrg?.title ?? emailDomain ?? '';
  const accountEmail = currentUser?.email ?? currentUser?.username ?? displayName;

  const handleProfilePopoverOpenChange = (open: boolean) => {
    if (open && profileRef.current) {
      const profileRect = profileRef.current.getBoundingClientRect();
      const sidebarElement = profileRef.current.closest<HTMLElement>('.ant-layout-sider');
      const sidebarRect = sidebarElement?.getBoundingClientRect();
      const leftInset = sidebarRect ? profileRect.left - sidebarRect.left : 0;
      setProfilePopoverWidth(sidebarRect ? sidebarRect.width - leftInset * 2 : profileRect.width);
    }
    setProfilePopoverOpen(open);
  };

  useEffect(() => {
    let mounted = true;

    const fetchOrgs = async () => {
      if (!currentUser?.currentOrganisationId) return;
      try {
        const res = await getOrganisations();
        if (mounted && res?.data) {
          setOrganisations(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) {
        console.error('Failed to fetch organisations for sidebar footer', e);
      }
    };

    fetchOrgs();

    return () => {
      mounted = false;
    };
  }, [currentUser?.currentOrganisationId]);

  return (
    <div className={`sidebar-user-footer ${collapsed ? 'sidebar-user-footer-collapsed' : ''}`}>
      <div ref={profileRef} className="sidebar-user-footer-profile">
        <Popover
          arrow={false}
          content={<SidebarProfilePopoverContent accountEmail={accountEmail} />}
          open={profilePopoverOpen}
          classNames={{ root: 'sidebar-user-footer-popover' }}
          placement={collapsed ? 'rightBottom' : 'topLeft'}
          styles={{ body: { padding: 0 }, root: { width: collapsed ? 180 : profilePopoverWidth } }}
          trigger="click"
          onOpenChange={handleProfilePopoverOpenChange}
        >
          <button
            type="button"
            className="sidebar-user-footer-profile-trigger"
            aria-label={collapsed ? 'Profile' : undefined}
          >
            <UserAvatar
              id={currentUser?.id ?? displayName}
              size={avatarSize}
              className="sidebar-user-footer-avatar"
            >
              {initial}
            </UserAvatar>
            <div className="sidebar-user-footer-copy">
              <div className="sidebar-user-footer-name">{displayName}</div>
              {organisationName && (
                <div className="sidebar-user-footer-org">{organisationName}</div>
              )}
            </div>
          </button>
        </Popover>
        {!collapsed && (
          <div className="sidebar-user-footer-logout">
            {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
            <LogoutButton
              collapsed
              iconColor="rgba(244, 239, 230, 0.72)"
              iconSize={primaryFooterIconSize}
            />
          </div>
        )}
      </div>
      <Divider className="sidebar-user-footer-divider" />
      <div className="sidebar-user-footer-links">
        <UserGuideButton
          collapsed={collapsed}
          actionClassName="sidebar-user-footer-link"
          iconColor={Colors.logoutColor}
          iconSize={userGuideIconSize}
          labelOverride="Insider"
        />
        <ReportBugButton
          collapsed={collapsed}
          actionClassName="sidebar-user-footer-link sidebar-user-footer-bug-link"
          iconColor="#ff6b65"
          iconSize={primaryFooterIconSize}
        />
        {!collapsed && (
          <>
            <AngelTeamsButton
              collapsed={false}
              actionClassName="sidebar-user-footer-link"
              iconColor={Colors.logoutColor}
              iconSize={primaryFooterIconSize}
            />
            <LanguageSwitcher
              collapsed={false}
              actionClassName="sidebar-user-footer-link"
              iconColor={Colors.logoutColor}
              iconSize={primaryFooterIconSize}
              labelOverride="Language"
            />
          </>
        )}
      </div>
    </div>
  );
};

export const LogoutButton: FC<FooterActionProps> = ({
  actionClassName: footerActionClassName,
  collapsed,
  iconColor,
  iconSize = 16,
}) => {
  const actionClassName = useEmotionCss(({ token }) => {
    return {
      color: Colors.textColorSecondary,
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
    };
  });
  const { initialState, setInitialState } = useModel('@@initialState');
  const intl = useIntl();
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const onLogout = useCallback(async () => {
    flushSync(() => setInitialState({ ...initialState, currentUser: undefined } as any));
    localStorage.removeItem('tokenRefresh');
    await logout();
    localStorage.removeItem('token');
    history.push('/user/logged-out');
  }, [setInitialState]);

  const loading = (
    <span className={actionClassName}>
      <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  if (!initialState?.currentUser?.username) {
    return loading;
  }

  if (showToopTip || collapsed) {
    return (
      <Tooltip
        title={intl.formatMessage({ id: 'common.logout', defaultMessage: 'Logout' })}
        placement="right"
      >
        <Button
          type="text"
          onClick={onLogout}
          style={{ color: Colors.logoutColor }}
          className={`logoutButton ${footerActionClassName ?? ''}`}
          icon={<LogoutIcon size={iconSize} color={iconColor} />}
          aria-label={intl.formatMessage({ id: 'common.logout', defaultMessage: 'Logout' })}
        ></Button>
      </Tooltip>
    );
  }

  return (
    <Button
      type="text"
      onClick={onLogout}
      style={{ color: Colors.logoutColor }}
      className={`logoutButton ${footerActionClassName ?? ''}`}
      icon={<LogoutIcon size={iconSize} color={iconColor} />}
    >
      <span style={{ fontSize: 14 }}>{intl.formatMessage({ id: 'logout' })}</span>
    </Button>
  );
};
